import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';

import {
  closeResetTemplateDialog,
  deleteWorkspaceEmailTemplateThunk,
} from '../../store/workspaceEmailTemplatesSlice';

import { useAppDispatch, useAppSelector } from '@store/hooks';

interface Props {
  tenantId: string;
  onDeleted: () => void;
}

const ResetEmailTemplateDialog: React.FC<Props> = ({ tenantId, onDeleted }) => {
  const dispatch = useAppDispatch();
  const { open, templateType, deleteStatus, deleteError } = useAppSelector(
    (s) => s.settingsWorkspaceEmailTemplates.resetDialog
  );

  const displayName = useAppSelector((s) => {
    const item = s.settingsWorkspaceEmailTemplates.list.find(
      (x) => x.templateType === templateType
    );
    return item?.displayName ?? templateType ?? 'template';
  });

  const handleClose = () => {
    dispatch(closeResetTemplateDialog());
  };

  const handleConfirm = async () => {
    if (!templateType) return;

    const resultAction = await dispatch(
      deleteWorkspaceEmailTemplateThunk({ tenantId, templateType })
    );

    if (deleteWorkspaceEmailTemplateThunk.fulfilled.match(resultAction)) {
      onDeleted();
      dispatch(closeResetTemplateDialog());
    }
  };

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-sm bg-white rounded-lg shadow-xl p-6">
              <Dialog.Title className="text-base font-semibold text-gray-900 mb-2">
                Reset Template
              </Dialog.Title>

              <p className="text-sm text-gray-600 mb-4">
                Remove custom template for{' '}
                <span className="font-medium text-gray-900">{displayName}</span>
                ? It will revert to the global default.
              </p>

              {deleteError && (
                <p className="mb-4 text-sm text-red-600">{deleteError}</p>
              )}

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={deleteStatus === 'loading'}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteStatus === 'loading' ? 'Resetting…' : 'Confirm Reset'}
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ResetEmailTemplateDialog;
